import * as KatalMetrics from '@amzn/katal-metrics';
import {
  NavMetricPublisher,
  AccountSwitcherPublisher,
} from 'src/commons/utils/metrics';
import { logger } from 'src/commons/utils/logger';

/**
 * Fire-and-forget metric emitters for slice 20.2's shadow diff. The render
 * path must never be affected — every call is wrapped so a metric failure
 * (missing driver in tests, misconfigured publisher, etc.) is swallowed.
 */

const safeCounter = (
  publisher: KatalMetrics.Publisher,
  name: string,
  value: number,
): void => {
  try {
    publisher.publishCounterMonitor(name, value);
  } catch (e) {
    logger.error(
      `shadow metric emit failed: ${name}`,
      e instanceof Error ? e : new Error(String(e)),
    );
  }
};

export type GlobalNavShadowOutcome =
  | 'Match'
  | 'Mismatch'
  | 'BtasError'
  | 'NavbarInitError'
  | 'DiffThrew';

export type AccountInfoShadowOutcome =
  | 'Match'
  | 'Mismatch'
  | 'BtasError'
  | 'AccountSwitcherInitError'
  | 'DiffThrew';

export const emitGlobalNavShadow = (outcome: GlobalNavShadowOutcome): void => {
  safeCounter(NavMetricPublisher, `Shadow.GlobalNav.${outcome}`, 1);
};

export const emitAccountInfoShadow = (
  outcome: AccountInfoShadowOutcome,
): void => {
  safeCounter(AccountSwitcherPublisher, `Shadow.AccountInfo.${outcome}`, 1);
};

// LatencyDelta was intentionally NOT emitted here. BTAS getNavState is fetched
// early in Nav.tsx (for CSRF) and the shadow diff sites await an already-in-
// flight promise, so measuring latency from "start of await" produces a wall-
// clock queue-time (near zero) rather than a real service RTT. A meaningful
// latency delta requires capturing the fetch start inside the API wrappers —
// deferred to a follow-up slice.
